import Badge from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/flags';
import Z_INDEX from '@databases/layout/z-index';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedEntireEntryIds,
  selectIsHoverDeleteEntriesButton,
  selectSelectedEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseLayoutType, DatabaseViewSettings } from '@databases/types/database/view';
import { getValidationError } from '@databases/utils/database/field-types';
import {
  getCoverField,
  getDatabaseEntryRequired,
  getDatabaseFields,
  getDatabaseValue,
  getShownFieldIdsForDatabaseCard,
} from '@databases/utils/database/utils';
import { getGroupField } from '@databases/utils/database/view/view';
import composeRefs from '@seznam/compose-react-refs';
import React, { CSSProperties, forwardRef, useRef, ForwardedRef, RefObject } from 'react';
import { createRoot, Root } from 'react-dom/client';
import styled from 'styled-components';

import { DragType, setPublicDragData } from '../../../drag-utils';
import InlineEditWrapper from '../../InlineEditWrapper';
import { useScope } from '../../Scope';
import { subtleBorder } from '../../styled';
import EntryCoverImage from '../EntryCoverImage';

import EntryCardContainer, { EntryCardShadow } from './EntryCardContainer';
import EntryCardValue from './EntryCardValue';
import { CONTAINER_PADDING } from './height';

const ENTRY_CARD_VALUES_PADDING = 4;
const ENTRY_CARD_VALUES_GAP = 12;
const ENTRY_CARD_BADGE_HEIGHT = 20;

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    display: flex;
    flex-direction: column;
  }
`;

const StyledInlineEditWrapper = styled(InlineEditWrapper)`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  width: 100%;
  background: ${token('elevation.surface.raised')};

  > * {
    flex-grow: 1;
  }
`;

const ValuesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${ENTRY_CARD_VALUES_PADDING}px;
  gap: ${ENTRY_CARD_VALUES_GAP}px;
  overflow-wrap: anywhere;
`;

export interface EntryCardProps {
  contentId: string;
  entryId: string;
  viewSettings?: DatabaseViewSettings;
  isReadonly?: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent, entryId: string) => void;
  onDrag?: (e: React.DragEvent, entryId: string) => void;
  onDragEnd?: (e: React.DragEvent, entryId: string) => void;
  style?: CSSProperties;
  handleSelectEntry: (e: React.MouseEvent) => void;
  handleOnContextMenu: (e: React.MouseEvent) => void;
}

export type TableLayoutHeaderCellRefObj = RefObject<
  EntryCardRefs<RefObject<HTMLDivElement | null>, RefObject<HTMLDivElement | null>>
>;

export type EntryCardRefs<H, T> = {
  entryCardRef: H;
  boardLayoutColumnRef?: T;
};

const EntryCard = forwardRef<
  EntryCardRefs<
    ForwardedRef<HTMLElement | null> | undefined,
    ForwardedRef<HTMLDivElement | null>
  >,
  EntryCardProps
>(function EntryCard(
  {
    contentId,
    entryId,
    viewSettings,
    isReadonly,
    isDraggable,
    isDragging,
    onDragStart,
    onDrag,
    onDragEnd,
    style,
    handleSelectEntry,
    handleOnContextMenu,
  },
  ref
) {
  const scope = useScope();

  const { showPanel, hidePanel } = usePanel();

  const [
    shownFieldIds,
    showCover,
    isInvalid,
    isEntrySelected,
    selectedEntryIds,
    isHoverDeleteEntriesButton,
  ] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const databaseDraftView = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      viewSettings: viewSettings,
      includeDeleted: false,
    });
    const coverField = getCoverField(databaseDraft, viewSettings?.layout.coverFieldId);
    const entry = getDatabaseEntryRequired(databaseDraft, entryId);
    const selectedEntries = selectSelectedEntryIds(state, {
      contentId,
      scope,
      viewSettings,
    });
    const fields = getDatabaseFields(databaseDraft);
    const shownFields = getDatabaseFields(databaseDraftView);
    const isEntrySelected = selectedEntries.includes(entryId);
    const groupField =
      viewSettings?.layout.type === DatabaseLayoutType.BOARD
        ? getGroupField(databaseDraft, viewSettings.layout)
        : undefined;

    return [
      getShownFieldIdsForDatabaseCard(shownFields, coverField, groupField),
      !!coverField,
      fields.some((field) => {
        const value = getDatabaseValue(databaseDraft, field, entry);
        return !!getValidationError(
          { database: databaseDraft, field, entry, value },
          state
        );
      }),
      isEntrySelected,
      selectSelectedEntireEntryIds(state, {
        contentId,
        scope,
        viewSettings,
      }),
      isEntrySelected && selectIsHoverDeleteEntriesButton(state),
    ] as const;
  });

  const closeDatabaseEntryPanel = useFlaggingCallback(() => {
    hidePanel();
  }, [hidePanel]);

  const openDatabaseEntryPanel = useFlaggingCallback(() => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly,
    });
  }, [entryId, isReadonly, showPanel]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const prevIsSelected = useRef(isEntrySelected);
  const handleMouseDown = useFlaggingCallback(
    (e: React.MouseEvent) => {
      const isRightClick = e.button === 2;
      if (!isEntrySelected && !isRightClick) {
        handleSelectEntry(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [handleSelectEntry, isEntrySelected]
  );

  const handleMouseUp = useFlaggingCallback(
    (e: React.MouseEvent) => {
      const isRightClick = e.button === 2;
      const isMultiSelectOn = isModifierKeyPressed(e, ['shiftKey']);
      if (isEntrySelected && !prevIsSelected.current && !isRightClick) {
        // remove selection if the user clicks on the select button again
        handleSelectEntry(e);
        closeDatabaseEntryPanel();
      } else if (!isDragging && !isMultiSelectOn) {
        // open the entry dialog only if the card is not being dragged
        openDatabaseEntryPanel();
      }
    },
    [
      closeDatabaseEntryPanel,
      handleSelectEntry,
      isDragging,
      isEntrySelected,
      openDatabaseEntryPanel,
    ]
  );

  const shadowProps = {
    as: 'a',
    href: undefined,
    target: undefined,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onContextMenu: handleOnContextMenu,
  };

  const dragImage = useRef<HTMLElement>();
  const cardImage = useRef<HTMLElement>();
  const badgeImage = useRef<HTMLElement>();
  const dragImageRootRef = useRef<Root>();
  const badgeImageRootRef = useRef<Root>();

  const handleDragStart = useFlaggingCallback(
    (e: React.DragEvent) => {
      if (e.target !== e.currentTarget) {
        // Ignore dragging descendent elements
        return;
      }

      closeDatabaseEntryPanel();

      if (selectedEntryIds.length > 1 && containerRef.current) {
        dragImage.current = document.createElement('div');
        cardImage.current = document.createElement('div');
        badgeImage.current = document.createElement('div');

        (
          ref as TableLayoutHeaderCellRefObj
        ).current?.boardLayoutColumnRef?.current?.append(dragImage.current);

        dragImageRootRef.current = createRoot(dragImage.current);
        badgeImageRootRef.current = createRoot(badgeImage.current);

        Object.assign(dragImage.current.style, {
          position: 'absolute',
          top: `${CONTAINER_PADDING + ENTRY_CARD_BADGE_HEIGHT / 2}px`,
          left: `${CONTAINER_PADDING + 1}px`,
          zIndex: Z_INDEX.DRAG_IMAGE_ELEMENT,
        });
        Object.assign(cardImage.current.style, {
          width: `${containerRef.current!.offsetWidth - ENTRY_CARD_BADGE_HEIGHT / 2}px`,
          height: `${containerRef.current!.offsetHeight}px`,
        });
        Object.assign(badgeImage.current.style, {
          position: 'absolute',
          top: `-${ENTRY_CARD_BADGE_HEIGHT / 2}px`,
          right: `-${ENTRY_CARD_BADGE_HEIGHT / 2}px`,
        });

        cardImage.current.append(containerRef.current.cloneNode(true));
        cardImage.current.append(badgeImage.current);
        dragImage.current.append(cardImage.current);

        badgeImageRootRef.current.render(
          <Badge appearance="primary">{selectedEntryIds.length}</Badge>
        );
        e.dataTransfer.setDragImage(dragImage.current, 0, 25);
      }

      setPublicDragData(e.dataTransfer, {
        type: DragType.ENTRY_CARD,
        contentId,
        draggingIds: selectedEntryIds,
        dragOverId: entryId,
        height: containerRef.current!.offsetHeight,
        width: containerRef.current!.offsetWidth,
      });

      // The browser takes a screenshot of the element to use as the drag image. The screenshot does not contain the
      // box shadow, so we add a temporary border to improve the style of the drag image.
      containerRef.current!.style.border = subtleBorder;
      requestAnimationFrame(() => {
        containerRef.current!.style.border = '';
      });

      onDragStart?.(e, entryId);
    },
    [closeDatabaseEntryPanel, contentId, entryId, onDragStart, ref, selectedEntryIds]
  );

  const handleDrag = useFlaggingCallback(
    (e: React.DragEvent) => {
      if (e.target !== e.currentTarget) {
        // Ignore dragging descendent elements
        return;
      }

      onDrag?.(e, entryId);
    },
    [entryId, onDrag]
  );

  const handleDragEnd = useFlaggingCallback(
    (e: React.DragEvent) => {
      if (e.target !== e.currentTarget) {
        // Ignore dragging descendent elements
        return;
      }

      if (dragImage.current) {
        dragImage.current.remove();
        dragImage.current = undefined;
      }
      if (cardImage.current) {
        cardImage.current.remove();
        cardImage.current = undefined;
      }
      if (badgeImage.current) {
        badgeImage.current.remove();
        badgeImage.current = undefined;
      }
      if (dragImageRootRef.current) {
        dragImageRootRef.current.unmount();
        dragImageRootRef.current = undefined;
      }
      if (badgeImageRootRef.current) {
        badgeImageRootRef.current.unmount();
        badgeImageRootRef.current = undefined;
      }

      onDragEnd?.(e, entryId);
    },
    [entryId, onDragEnd]
  );

  return (
    <StyledSkeletonItem isButton>
      <StyledInlineEditWrapper
        markAsDeleted={isHoverDeleteEntriesButton}
        isInvalid={isInvalid}
        isSelected={isEntrySelected}
        ref={composeRefs(
          containerRef,
          (ref as TableLayoutHeaderCellRefObj)?.current?.entryCardRef
        )}
        isDraggable={isDraggable}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={style}
      >
        <EntryCardContainer>
          <EntryCardShadow draggable="false" {...(shadowProps as any)} />
          {showCover && (
            <EntryCoverImage
              contentId={contentId}
              entryId={entryId}
              viewSettings={viewSettings}
            />
          )}
          <ValuesWrapper>
            {shownFieldIds.map((fieldId) => (
              <EntryCardValue
                key={fieldId}
                contentId={contentId}
                entryId={entryId}
                fieldId={fieldId}
                isReadonly={isReadonly}
              />
            ))}
          </ValuesWrapper>
        </EntryCardContainer>
      </StyledInlineEditWrapper>
    </StyledSkeletonItem>
  );
});

export default EntryCard;
